import type { PlanKey } from "@giftforward/entitlements";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchCampaignDraft } from "@/api/campaignBuilder";
import ContactEmailPreviewModal from "@/components/admin/campaigns/ContactEmailPreviewModal";
import RemoveContactModal from "@/components/admin/campaigns/RemoveContactModal";
import ToastChip from "@/components/campaignBuilder/ToastChip";
import AddRecipientsMenu from "@/components/contacts/AddRecipientsMenu";
import AddRecipientWizard from "@/components/contacts/AddRecipientWizard";
import {
  addOneSubtitle,
  addRecipientsMenuVisible,
  mergeNewContacts,
  recordAdded,
} from "@/components/contacts/addRecipients-helper";
import BudgetHistoryModal from "@/components/contacts/BudgetHistoryModal";
import BudgetStrip from "@/components/contacts/BudgetStrip";
import BulkImportDialogs, { useBulkImport } from "@/components/contacts/BulkImportDialogs";
import CampaignContactsModal from "@/components/contacts/CampaignContactsModal";
import { contactFieldHint, contactSavedToast } from "@/components/contacts/campaignContacts-helper";
import {
  contactRemoveFlow,
  removeBlockedReason,
  removeVisible,
} from "@/components/contacts/contactRemove-helper";
import RecipientLimitDialog from "@/components/contacts/RecipientLimitDialog";
import type { CampaignContactRow, CampaignContactsLock } from "@/components/contacts/types";
import { ENDED_CAMPAIGN_STATUSES } from "@/components/giftLinks/giftLinks-helper";
import { CAMPAIGN_BUILDER_ID_KEY } from "@/constants/campaignBuilder";
import { COPY } from "@/constants/campaignContacts";
import { CAMPAIGN_STATUSES } from "@/constants/campaings";
import { useCampaignEndpoints } from "@/context/CampaignEndpointsContext";
import { useCampaignContactActions, useCampaignContacts } from "@/hooks/useCampaignContacts";
import { useRecipientCap } from "@/hooks/useRecipientCap";
import type { Recipient, Stage3EmailState } from "@/interfaces/CampaignBuilder";
import type {
  AddedRowsState,
  CampaignContactWire,
  UpdateCampaignContactBody,
} from "@/interfaces/campaignContacts";
import type { CampaignDraftDetail } from "@/interfaces/campaignDraft";
import type { CampaignStatus } from "@/types/types";
import { apiErrorMessage } from "@/utils/apiError-helper";
import { capDialogOnAdd } from "@/utils/recipient-cap-helper";
import { resolveScheduleTimeZone } from "@/utils/timezone-helper";

interface ManageContactsModalProps {
  open: boolean;
  onClose: () => void;
  campaignId: string;
  campaignName: string;
  /**
   * The campaign's per-gift price (collection tier). When given, the Export CSV download is named
   * `Campaign Name - $Price.csv`; omitted (admin screens with no price to hand), it keeps the
   * `campaign-<slug>-contacts.csv` scheme.
   */
  pricePerGift?: number;
  campaignStatus: CampaignStatus;
  /** Manual Distribution: GFG sends nothing, so there is no resend. */
  manual: boolean;
  /** The card's screen-only pause flag. */
  paused: boolean;
  /** May the caller edit / send on THIS campaign? False opens the list read-only. */
  canEdit: boolean;
  canSend: boolean;
  /** GFG admin: Add recipients is offered only while freed slots exist, never to grow the budget. */
  replacementOnly?: boolean;
}

interface Toast {
  message: string;
  variant: "success" | "error";
}

type RemoveMutation = ReturnType<typeof useCampaignContactActions>["remove"];

/** The confirmation in front of a removal; `open` flips alone so the copy survives the fade. */
interface RemoveTarget {
  row: CampaignContactWire;
  open: boolean;
}

const NO_ADDED_ROWS: AddedRowsState = { newIds: [], resetSignal: 0 };

/** An error toast holds a sentence to read, so it stays up longer than ToastChip's 3200ms default. */
const ERROR_TOAST_MS = 8000;

/** Wire row -> the modal's view model. */
function toContactRow(row: CampaignContactWire, automation: boolean): CampaignContactRow {
  return {
    id: row.id,
    name: row.name,
    email: row.email,
    status: row.status,
    firstName: row.firstName,
    lastName: row.lastName,
    // ALWAYS present, even when null: `campaignContactsColumns` (see campaignContacts-helper.ts)
    // decides whether a column renders by KEY PRESENCE, not truthiness. Multi-date campaigns show
    // Send Date in place of Delivered (the time the gift email went out, null until sent).
    ...(automation ? { sendDate: row.sendDate } : { deliveredAt: row.sentAt }),
    campaignUrl: row.redemptionLink,
    resentAt: row.resentAt,
    openedAt: row.openedAt ?? null,
  };
}

/** The campaign draft's email block -> the preview's state, missing fields falling back to empty. */
function toEmailState(draft: CampaignDraftDetail | undefined): Stage3EmailState | undefined {
  if (!draft) return undefined;
  const e = draft.email ?? {};
  return {
    subject: e.subject ?? "",
    messageHtml: e.messageHtml ?? "",
    signatureHtml: e.signatureHtml ?? "",
    ctaColor: e.ctaColor ?? "",
    logoEnabled: e.logoEnabled ?? false,
    logoUrl: e.logoUrl ?? "",
    logoOriginalUrl: e.logoOriginalUrl ?? "",
    logoPadding: e.logoPadding ?? 0,
    bannerEnabled: e.bannerEnabled ?? false,
    bannerUrl: e.bannerUrl ?? "",
    bannerOriginalUrl: e.bannerOriginalUrl ?? "",
  };
}

/**
 * The recipient the preview resolves its @tokens for: the draft recipient with the contact's email
 * (case-insensitive), else the contact's own names/email with the first draft recipient's sender.
 */
function toPreviewRecipient(
  contact: CampaignContactRow,
  draft: CampaignDraftDetail | undefined,
): Recipient {
  const recipients = draft?.recipients ?? [];
  const match = recipients.find((r) => r.email.toLowerCase() === contact.email.toLowerCase());
  const source = match ?? recipients[0];
  return {
    id: String(contact.id),
    firstName: match?.firstName ?? contact.firstName ?? contact.name,
    lastName: match?.lastName ?? contact.lastName ?? "",
    email: contact.email,
    toCompany: match?.toCompany,
    senderFirst: source?.senderFirst ?? "",
    senderLast: source?.senderLast ?? "",
    senderCompany: source?.senderCompany ?? "",
    fromEmail: source?.fromEmail,
    senderSignature: source?.senderSignature,
  };
}

/**
 * The row × removes the contact (GFG-364 QA R2). Every removable status confirms first, with the
 * no-refund wording; Redeemed, ended and last rows never reach `start`: `removeBlockedReason`
 * refuses them in the table. A rejection surfaces as an error toast with the server's sentence.
 */
function useRemoveFlow(i: {
  rows: readonly CampaignContactWire[];
  remove: RemoveMutation;
  notify: (message: string, variant: Toast["variant"]) => void;
}) {
  const [target, setTarget] = useState<RemoveTarget | null>(null);
  const byId = useMemo(() => new Map(i.rows.map((r) => [r.id, r])), [i.rows]);

  function start(contact: CampaignContactRow) {
    const row = byId.get(Number(contact.id));
    if (row === undefined) return;
    if (contactRemoveFlow(contact, i.rows.length, row.allowed.remove) === "confirm") {
      setTarget({ row, open: true });
    }
  }

  async function confirm() {
    if (target === null) return;
    try {
      await i.remove.mutateAsync(target.row.id);
      i.notify(COPY.remove.success, "success");
    } catch (err) {
      i.notify(apiErrorMessage(err), "error");
    }
    setTarget({ ...target, open: false });
  }

  return {
    target,
    start,
    confirm,
    cancel: () => setTarget((t) => (t === null ? t : { ...t, open: false })),
  };
}

function useToast() {
  const [toast, setToast] = useState<Toast | null>(null);
  const [open, setOpen] = useState(false);
  // Bumped on every notification so the chip, keyed by it, remounts with a fresh timer and animation.
  const [sequence, setSequence] = useState(0);
  function notify(message: string, variant: Toast["variant"]) {
    setToast({ message, variant });
    setSequence((n) => n + 1);
    setOpen(true);
  }
  return { toast, open, sequence, notify, close: () => setOpen(false) };
}

type Notify = ReturnType<typeof useToast>["notify"];

/** Rows added or swapped in while the modal is open: on top with a NEW tag (B10), cleared on close. */
function useAddedRows(notify: Notify) {
  const [added, setAdded] = useState<AddedRowsState>(NO_ADDED_ROWS);
  return {
    added,
    markAdded(ids: readonly number[], message: string) {
      setAdded((state) => recordAdded(state, ids));
      notify(message, "success");
    },
    clearNew: () => setAdded((state) => ({ ...state, newIds: [] })),
  };
}

type ContactActions = Pick<ReturnType<typeof useCampaignContactActions>, "update" | "resend">;

/** Per contact: the in-flight/last email write, the last address committed, and the ids with a resend in flight. */
interface EditGuards {
  emailWrites: Map<number, Promise<unknown>>;
  committedEmails: Map<number, string>;
  busyIds: Set<number>;
}

async function writeContact(
  g: EditGuards,
  c: { update: ContactActions["update"]; notify: Notify },
  contactId: string | number,
  body: UpdateCampaignContactBody,
  contactName: string,
) {
  const id = Number(contactId);
  const write = c.update.mutateAsync({ contactId: id, body });
  if (body.email !== undefined) {
    g.emailWrites.set(
      id,
      write.catch(() => undefined),
    );
    g.committedEmails.set(id, body.email);
  }
  try {
    await write;
  } catch (err) {
    if (body.email !== undefined) g.committedEmails.delete(id);
    c.notify(apiErrorMessage(err), "error");
    // Name and send-date edits rethrow: a name editor forgets the pair, the send-date input reverts,
    // and the next commit retries.
    if (body.email === undefined) throw err;
    return;
  }
  c.notify(contactSavedToast(body, contactName), "success");
}

async function resendContact(
  g: EditGuards,
  c: { actions: ContactActions; notify: Notify },
  contact: CampaignContactRow,
  email: string,
) {
  const id = Number(contact.id);
  // Thrown (not returned) so the modal does not show its success toast; nothing to report.
  if (g.busyIds.has(id)) throw new Error("resend already in flight");
  g.busyIds.add(id);
  try {
    await g.emailWrites.get(id);
    if (email !== contact.email && email !== g.committedEmails.get(id)) {
      await c.actions.update.mutateAsync({ contactId: id, body: { email } });
      g.committedEmails.set(id, email);
    }
    await c.actions.resend.mutateAsync(id);
  } catch (err) {
    c.notify(apiErrorMessage(err), "error");
    // Re-thrown so the modal does not show its own success toast.
    throw err;
  } finally {
    g.busyIds.delete(id);
  }
}

/** The inline name/email/send-date write and the resend, ordered per contact through refs. */
function useContactEdits(actions: ContactActions, notify: Notify) {
  const guards = useRef<EditGuards>({
    emailWrites: new Map(),
    committedEmails: new Map(),
    busyIds: new Set(),
  });
  return {
    runUpdate: (contact: CampaignContactRow, body: UpdateCampaignContactBody) =>
      writeContact(
        guards.current,
        { update: actions.update, notify },
        contact.id,
        body,
        contact.name,
      ),
    handleResend: (contact: CampaignContactRow, email: string) =>
      resendContact(guards.current, { actions, notify }, contact, email),
  };
}

/** The email preview target and the draft it resolves its @tokens against (fetched only while open). */
function useEmailPreview(campaignId: string) {
  const [target, setTarget] = useState<CampaignContactRow | null>(null);
  const draftPath = useCampaignEndpoints().draft(campaignId);
  const draftQuery = useQuery({
    queryKey: ["campaignDraft", campaignId, draftPath],
    queryFn: () => fetchCampaignDraft(campaignId, draftPath),
    enabled: target !== null,
    staleTime: 0,
  });
  const email = useMemo(() => toEmailState(draftQuery.data), [draftQuery.data]);
  const recipients = useMemo(
    () => (target ? [toPreviewRecipient(target, draftQuery.data)] : undefined),
    [target, draftQuery.data],
  );
  return { target, setTarget, draftQuery, email, recipients };
}

/** Wire rows -> the table's memoized view models. */
function useContactRows(data: ContactsEnvelope | undefined, newIds: readonly string[]) {
  // Memoized: `data` has a stable reference from TanStack Query, so without this the mapped array
  // gets a new identity on every render of this always-mounted container (one per card), which
  // would also defeat any memoization the reused modal does internally on its `contacts` prop.
  const rows = useMemo(() => data?.contacts ?? [], [data]);
  const contacts = useMemo(
    () =>
      mergeNewContacts(
        rows.map((r) => toContactRow(r, !!data?.automation)),
        newIds,
      ),
    [rows, data?.automation, newIds],
  );
  const newContactIds = useMemo(() => new Set(newIds), [newIds]);
  const existingEmails = useMemo(() => rows.map((r) => r.email), [rows]);
  const allowedById = useMemo(() => new Map(rows.map((r) => [r.id, r.allowed])), [rows]);
  return { rows, contacts, newContactIds, existingEmails, allowedById };
}

/** What the viewer may do given the campaign's status, the card's pause flag and manual mode. */
function useContactGates(
  data: ContactsEnvelope | undefined,
  i: {
    campaignStatus: CampaignStatus;
    manual: boolean;
    paused: boolean;
    canEdit: boolean;
    canSend: boolean;
  },
) {
  // The server's status wins once loaded: an unpaid draft can still read "scheduled" on the card.
  const status = data?.campaign.status ?? i.campaignStatus;
  // `paused` is the card's screen-only pause: the server cannot see it, so only this client-side
  // lock stops actions while it is on. A closed, cancelled or archived campaign has ended.
  const lock: CampaignContactsLock | null = i.paused
    ? "paused"
    : ENDED_CAMPAIGN_STATUSES.includes(status)
      ? "closed"
      : null;
  // Inline edits and resend exist only once a campaign has been (or is about to be) sent; a closed
  // one shows them inert under the ended banner. A draft only takes additions (contract §3).
  const mutable =
    status === CAMPAIGN_STATUSES.SCHEDULED ||
    status === CAMPAIGN_STATUSES.ACTIVE ||
    status === CAMPAIGN_STATUSES.CLOSED;
  const canEditCampaign = i.canEdit;
  return {
    status,
    draft: status === CAMPAIGN_STATUSES.DRAFT,
    lock,
    canEditCampaign,
    canEdit: mutable && canEditCampaign,
    canResend: mutable && i.canSend && !i.manual,
    canRemove: removeVisible({ canEdit: canEditCampaign, status, manual: i.manual }),
    // Manual Distribution is out of scope (spec): it gets neither additions nor the budget strip.
    budget: i.manual ? null : (data?.budget ?? null),
  };
}

type ContactsEnvelope = NonNullable<ReturnType<typeof useCampaignContacts>["data"]>;
type ContactGates = ReturnType<typeof useContactGates>;
type ContactRows = ReturnType<typeof useContactRows>;
type ContactEdits = ReturnType<typeof useContactEdits>;
type RemoveFlow = ReturnType<typeof useRemoveFlow>;
type BulkImport = ReturnType<typeof useBulkImport>;

interface TableProps {
  open: boolean;
  campaignName: string;
  pricePerGift?: number;
  query: ReturnType<typeof useCampaignContacts>;
  view: ContactRows;
  gates: ContactGates;
  edits: ContactEdits;
  removeFlow: RemoveFlow;
  manual: boolean;
  replacementOnly: boolean;
  resetSignal: number;
  onClose: () => void;
  onPreviewEmail: (contact: CampaignContactRow) => void;
  onHistory: () => void;
  onAddOne: () => void;
  onBulk: () => void;
}

/** The toolbar budget strip and the footer "add recipients" menu, each only when it applies. */
function tableExtras(p: TableProps) {
  const { data } = p.query;
  const { budget, draft, status, lock, canEditCampaign } = p.gates;
  const showMenu =
    data &&
    addRecipientsMenuVisible({
      canEdit: canEditCampaign,
      canAddRecipients:
        data.canAddRecipients && (!p.replacementOnly || (!draft && data.spacesFree > 0)),
      status,
      manual: p.manual,
      lock,
    });
  return {
    toolbarEnd:
      data && budget ? (
        <BudgetStrip
          giftsBudgeted={budget.giftsBudgeted}
          spacesFree={data.spacesFree}
          pricePerGift={budget.pricePerGift}
          amendmentCount={data.budget?.amendmentCount ?? 0}
          onOpenHistory={p.onHistory}
        />
      ) : undefined,
    footerAction:
      data && showMenu ? (
        <AddRecipientsMenu
          subtitleOne={addOneSubtitle({
            draft,
            spacesFree: data.spacesFree,
            pricePerGift: data.budget?.pricePerGift ?? 0,
            replacementOnly: p.replacementOnly,
          })}
          onAddOne={p.onAddOne}
          onBulk={p.onBulk}
        />
      ) : undefined,
  };
}

function ManageContactsTable(p: TableProps) {
  const { gates, edits, removeFlow, view } = p;
  const { isLoading, isError, error } = p.query;
  const { canEdit } = gates;
  const allowed = (c: CampaignContactRow) => view.allowedById.get(Number(c.id));
  return (
    <CampaignContactsModal
      open={p.open}
      onClose={p.onClose}
      campaignName={p.campaignName}
      pricePerGift={p.pricePerGift}
      contacts={view.contacts}
      loading={isLoading}
      error={isError ? apiErrorMessage(error) : null}
      lock={gates.lock}
      onPreviewEmail={p.onPreviewEmail}
      onResend={gates.canResend ? edits.handleResend : undefined}
      onRemove={gates.canRemove ? removeFlow.start : undefined}
      removeBlockedReason={(c) => removeBlockedReason(c, view.rows.length, allowed(c)?.remove)}
      onEmailChange={canEdit ? (c, email) => edits.runUpdate(c, { email }) : undefined}
      onNameChange={
        canEdit
          ? (c, firstName, lastName) => edits.runUpdate(c, { firstName, lastName })
          : undefined
      }
      canEditName={(c) => allowed(c)?.editName ?? false}
      canEditEmail={(c) => allowed(c)?.editEmail ?? false}
      fieldHint={(field, c) =>
        contactFieldHint(
          field,
          c.status,
          gates.lock,
          c.name,
          (field === "name" ? allowed(c)?.editName : allowed(c)?.editEmail) ?? false,
        ) ?? undefined
      }
      onSendDateChange={canEdit ? (c, date) => edits.runUpdate(c, { sendDate: date }) : undefined}
      canEditSendDate={(c) => allowed(c)?.editSendDate ?? false}
      timeZone={resolveScheduleTimeZone(p.query.data?.campaign.scheduledTz)}
      expireDate={p.query.data?.campaign.expireDate ?? null}
      newContactIds={view.newContactIds}
      resetSignal={p.resetSignal}
      {...tableExtras(p)}
    />
  );
}

interface DialogsProps {
  campaignId: string;
  campaignName: string;
  data: ContactsEnvelope | undefined;
  existingEmails: readonly string[];
  removeFlow: RemoveFlow;
  removePending: boolean;
  preview: ReturnType<typeof useEmailPreview>;
  toast: ReturnType<typeof useToast>;
  wizardOpen: boolean;
  onWizardClose: () => void;
  bulk: BulkImport;
  historyOpen: boolean;
  onHistoryClose: () => void;
  onAdded: (ids: readonly number[], message: string) => void;
  /** The "Contact limit reached" dialog, open when an add was refused client-side (GFG-439). */
  limit: { open: boolean; cap: number | null; planKey: PlanKey | null; onClose: () => void };
}

function RemoveDialog(p: DialogsProps) {
  const { removeFlow } = p;
  return (
    <RemoveContactModal
      contact={removeFlow.target?.row ?? null}
      lock={null}
      open={removeFlow.target?.open ?? false}
      onClose={removeFlow.cancel}
      onConfirm={removeFlow.confirm}
      pending={p.removePending}
      warning={COPY.remove.confirmParagraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
    />
  );
}

function PreviewDialog(p: Pick<DialogsProps, "campaignId" | "campaignName" | "preview">) {
  const { preview } = p;
  const first = preview.recipients?.[0];
  const target = preview.target;
  return (
    <ContactEmailPreviewModal
      organization={first?.senderCompany || first?.senderFirst || p.campaignName}
      contact={target ? { id: Number(target.id), name: target.name, email: target.email } : null}
      open={target !== null}
      onClose={() => preview.setTarget(null)}
      preview={{
        data: preview.email,
        isLoading: preview.draftQuery.isLoading,
        isError: preview.draftQuery.isError,
        error: preview.draftQuery.error,
      }}
      recipients={preview.recipients}
    />
  );
}

/**
 * Everything that opens over the table: wizards, bulk importer, remove confirmation, history,
 * preview, toast.
 */
function ManageContactsDialogs(p: DialogsProps) {
  const { data, toast } = p;
  return (
    <>
      {/* The wizard closes itself after `onAdded`; a draft only gets its details step. */}
      {data && (
        <AddRecipientWizard
          open={p.wizardOpen}
          onClose={p.onWizardClose}
          campaignId={p.campaignId}
          envelope={data}
          entry="single"
          onAdded={(result, message) => p.onAdded(result.contactIds, message)}
        />
      )}
      <BulkImportDialogs {...p} />
      {p.limit.cap !== null && p.limit.planKey !== null && (
        <RecipientLimitDialog
          open={p.limit.open}
          cap={p.limit.cap}
          planKey={p.limit.planKey}
          onClose={p.limit.onClose}
        />
      )}
      <RemoveDialog {...p} />
      <BudgetHistoryModal
        open={p.historyOpen}
        onClose={p.onHistoryClose}
        campaignId={p.campaignId}
        campaignName={p.campaignName}
        timeZone={resolveScheduleTimeZone(data?.campaign.scheduledTz)}
      />
      <PreviewDialog {...p} />
      <ToastChip
        key={toast.sequence}
        message={toast.toast?.message ?? ""}
        open={toast.open}
        onClose={toast.close}
        variant={toast.toast?.variant}
        duration={toast.toast?.variant === "error" ? ERROR_TOAST_MS : undefined}
      />
    </>
  );
}

interface ManageCore {
  toast: ReturnType<typeof useToast>;
  addedRows: ReturnType<typeof useAddedRows>;
  actions: ReturnType<typeof useCampaignContactActions>;
  query: ReturnType<typeof useCampaignContacts>;
  view: ContactRows;
  gates: ContactGates;
  edits: ContactEdits;
  preview: ReturnType<typeof useEmailPreview>;
  removeFlow: RemoveFlow;
  bulk: BulkImport;
}

function useManageCore(p: ManageContactsModalProps): ManageCore {
  const toast = useToast();
  const addedRows = useAddedRows(toast.notify);
  const actions = useCampaignContactActions(p.campaignId);
  // Fetch only while the modal is open — the card behind it renders regardless of whether its
  // contacts have ever been requested.
  const query = useCampaignContacts(p.campaignId, p.open);
  const view = useContactRows(query.data, addedRows.added.newIds);
  const gates = useContactGates(query.data, p);
  const edits = useContactEdits(actions, toast.notify);
  const preview = useEmailPreview(p.campaignId);
  const removeFlow = useRemoveFlow({
    rows: view.rows,
    remove: actions.remove,
    notify: toast.notify,
  });
  const bulk = useBulkImport({
    data: query.data,
    existingEmails: view.existingEmails,
    notify: toast.notify,
  });
  return { toast, addedRows, actions, query, view, gates, edits, preview, removeFlow, bulk };
}

interface DialogFlags {
  wizardOpen: boolean;
  historyOpen: boolean;
  limitOpen: boolean;
  recipientCap: ReturnType<typeof useRecipientCap>;
  setWizardOpen: (open: boolean) => void;
  setHistoryOpen: (open: boolean) => void;
  setLimitOpen: (open: boolean) => void;
}

function dialogsProps(p: ManageContactsModalProps, core: ManageCore, f: DialogFlags): DialogsProps {
  return {
    campaignId: p.campaignId,
    campaignName: p.campaignName,
    data: core.query.data,
    existingEmails: core.view.existingEmails,
    removeFlow: core.removeFlow,
    removePending: core.actions.remove.isPending,
    preview: core.preview,
    toast: core.toast,
    wizardOpen: f.wizardOpen,
    onWizardClose: () => f.setWizardOpen(false),
    bulk: core.bulk,
    historyOpen: f.historyOpen,
    onHistoryClose: () => f.setHistoryOpen(false),
    onAdded: core.addedRows.markAdded,
    limit: {
      open: f.limitOpen,
      cap: f.recipientCap.cap,
      planKey: f.recipientCap.planKey,
      onClose: () => f.setLimitOpen(false),
    },
  };
}

/** The table and dialog props, wired to the shared state. */
function useManageContacts(p: ManageContactsModalProps) {
  const navigate = useNavigate();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [limitOpen, setLimitOpen] = useState(false);
  const recipientCap = useRecipientCap();
  const core = useManageCore(p);
  const { campaignId, campaignName } = p;
  // GFG-439: a launched campaign already at its plan's cap gets the limit dialog, not the wizard.
  // The envelope lists enabled contacts only, so its length is the count the cap applies to. GFG
  // staff (`replacementOnly`) only refill freed slots; the server still enforces the cap.
  const atCap =
    core.query.data !== undefined &&
    capDialogOnAdd({
      cap: recipientCap.cap,
      current: core.query.data.contacts.length,
      replacementOnly: p.replacementOnly ?? false,
      draft: core.gates.draft,
    });

  function handleClose() {
    core.addedRows.clearNew();
    p.onClose();
  }

  // A draft's recipients live in the builder, which loads the stashed id on mount (contract §3);
  // a launched campaign gets the importer over this modal, which stays open behind it.
  function handleBulk() {
    if (atCap) {
      setLimitOpen(true);
      return;
    }
    if (!core.gates.draft) {
      core.bulk.openImporter();
      return;
    }
    handleClose();
    sessionStorage.setItem(CAMPAIGN_BUILDER_ID_KEY, campaignId);
    navigate("/campaign/create/recipients");
  }

  const table: TableProps = {
    open: p.open,
    campaignName,
    pricePerGift: p.pricePerGift,
    query: core.query,
    view: core.view,
    gates: core.gates,
    edits: core.edits,
    removeFlow: core.removeFlow,
    manual: p.manual,
    replacementOnly: p.replacementOnly ?? false,
    resetSignal: core.addedRows.added.resetSignal,
    onClose: handleClose,
    onPreviewEmail: core.preview.setTarget,
    onHistory: () => setHistoryOpen(true),
    onAddOne: () => (atCap ? setLimitOpen(true) : setWizardOpen(true)),
    onBulk: handleBulk,
  };
  const dialogs = dialogsProps(p, core, {
    wizardOpen,
    historyOpen,
    limitOpen,
    recipientCap,
    setWizardOpen,
    setHistoryOpen,
    setLimitOpen,
  });
  return { table, dialogs };
}

/**
 * The My Campaigns card's contacts modal: a container over `CampaignContactsModal`. It owns the
 * fetch, the wire -> view-model mapping and the action wiring (edit name/email, resend, remove,
 * add recipients, budget history, email preview); the modal owns the table, filters, search,
 * locks and CSV export.
 */
export default function ManageContactsModal(props: ManageContactsModalProps) {
  const { table, dialogs } = useManageContacts(props);
  return (
    <>
      <ManageContactsTable {...table} />
      <ManageContactsDialogs {...dialogs} />
    </>
  );
}
